import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faUser,
  faUsers,
  faRightFromBracket,
  faArrowLeft,
  faUserShield,
  faArrowRight,
  faInfoCircle,
  faAngleDown, // Import angle down icon
  faAngleUp,   // Import angle up icon
  faEllipsisV, // Import vertical ellipsis icon
  faLink, // Import link icon
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns"; // Import date-fns for date formatting
import DOMPurify from "dompurify"; // Import DOMPurify for sanitizing content
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css"; // Import Toastify CSS
import Loader from "../components/Loader"; // Import Loader component
import { useParams } from "react-router-dom";
import BASE_URL from "../config";
import "../styles/Information.css"; // Import the custom CSS file

const ClientsInformation = () => {

    const { id } = useParams();

  // State for client-related data
  const [activeTab, setActiveTab] = useState(1);
  const [clientTabs, setClientTabs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsDropdown, setClientsDropdown] = useState([]);
  const [search, setSearch] = useState("");

  // UI state
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);

  // Editing state
  const [editingClient, setEditingClient] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editedName, setEditedName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // New info state
  const [newInfo, setNewInfo] = useState({
    type: "credentials",
    name: "",
    content: "",
    created_by: null,
    client_id: "",
  });

  // --- Utils ---
  const getToken = () => localStorage.getItem("token");

  // --- Redirect unauthenticated users ---
  useEffect(() => {
    const token = getToken();
    if (!token) {
      window.location.href = "/login";
    }
  }, []);

  // --- Decode token & set user ID ---
  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setNewInfo((prev) => ({ ...prev, created_by: payload.id }));
      } catch (error) {
        console.error("Token decoding failed:", error);
      }
    }
  }, []);

  // --- Fetch client tabs ---
  useEffect(() => {
    const fetchClientTabs = async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${BASE_URL}api/clients-info/clients-names`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const sortedTabs = res.data.sort((a, b) => (a.id === 1 ? -1 : b.id === 1 ? 1 : 0));
        setClientTabs(sortedTabs);
      } catch (error) {
        console.error("Client tabs error:", error);
        Swal.fire("Error", "Failed to fetch client tabs.", "error");
      }
    };

    fetchClientTabs();
  }, []);

  // --- Fetch clients based on active tab or search id ---
  useEffect(() => {
    const fetchClients = async () => {
      setClients([]);
      setLoading(true);

      try {
        const token = getToken();
        let endpoint = id
          ? `${BASE_URL}api/clients-info/find-all`
          : `${BASE_URL}api/clients-info/find-by-client/${activeTab}`;

        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const isJSON = res.headers["content-type"]?.includes("application/json");
        if (!isJSON) {
          Swal.fire("Error", "Unexpected server response.", "error");
          return;
        }

        let data = Array.isArray(res.data) ? res.data : [];

        // Apply custom filtering if ID is present in URL
        let filtered = id
          ? data.filter((item) =>
              [item.DatabaseInfo, item.Domain, item.Emails, item.HostigValue, item.Hosting, item.FTPInfo, item.HostingBy, item.Others, item.Registrar, item.SVN, item.WPAdminCredentials]
                .some(field => (field || "").includes(id))
            )
          : data;

        if (filtered.length === 0) filtered = data;

        if (filtered.length > 0) {
          const matched = clientTabs.find(tab => tab.id === filtered[0]?.Client);
          if (matched && matched.id !== activeTab) {
            setActiveTab(matched.id);
          }
        } else {
          console.warn("No client data found.");
        }

        setClients(filtered);
      } catch (error) {
        console.error("Fetch clients failed:", error);
        Swal.fire("Error", error.response?.data?.message || "Failed to fetch clients.", "error");
      } finally {
        setLoading(false);
        setSearch(""); // Clear search bar
      }
    };

    fetchClients();
  }, [activeTab]);

  // --- Fetch dropdown clients list ---
  useEffect(() => {
    const fetchDropdownClients = async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${BASE_URL}api/clients/find-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClientsDropdown(res.data);
      } catch (error) {
        console.error("Dropdown clients fetch error:", error);
        Swal.fire("Error", "Failed to fetch clients.", "error");
      }
    };

    fetchDropdownClients();
  }, []);

  const toggleCard = (cardId) => {
    setExpandedCard((prev) => (prev === cardId ? null : cardId)); // Toggle between expand and collapse
  };

  const copyToClipboardWithToast = (key, value) => {
    const sanitizedValue = DOMPurify.sanitize(value || "N/A").replace(/<[^>]*>/g, ""); // Remove HTML tags
    navigator.clipboard.writeText(sanitizedValue);
    toast.success(`${key} copied to clipboard!`, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      style: { backgroundColor: "#ffffff", color: "#006400" }, // Dark green background with white text
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "N/A"; // Return a default value if the date is missing
    }

    try {
      return format(new Date(dateString), "MMMM dd, yyyy"); // Format as "Month Day, Year Hour:Minute AM/PM"
    } catch (error) {
      console.error("Invalid date value:", dateString, error); // Log the invalid date for debugging
      return "Invalid Date"; // Return a fallback value for invalid dates
    }
  };

  const progressColors = ["bg-danger", "bg-success", "bg-warning", "bg-info"]; // Use Bootstrap color classes

  const renderProgressBar = (cardIndex) => {
    const color = progressColors[cardIndex % progressColors.length]; // Cycle through colors
    return (
      <div className="progress" style={{ height: "5px", backgroundColor: "#ffffff" }}>
        <div
          className={`progress-bar ${color}`} // Apply Bootstrap color class
          role="progressbar"
          style={{ width: "25%" }}
          aria-valuenow="25"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    );
  };

  const deleteRecord = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const endpoint = `${BASE_URL}api/clients-info/delete/${id}`;
        await axios.delete(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        toast.success("The record has been deleted successfully.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setClients(clients.filter((client) => client.id !== id)); // Update the client list
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete the record.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  };

  const startEditing = (client) => {
    setEditingClient(client); // Set the client being edited
    setEditedName(client.name); // Pre-fill the name with the current value
    setEditedContent(client.content); // Pre-fill the content with the current value
  };

  const updateRecord = async () => {
    if (!editingClient) return;

    // Validation for required fields
    if (!editingClient.Client || !editingClient.Client.trim()) {
      toast.error("Client name is required.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    if (!editingClient.Domain || !editingClient.Domain.trim()) {
      toast.error("Domain is required.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    // Check if there are any changes
    const originalClient = clients.find((client) => client.id === editingClient.id);
    if (JSON.stringify(originalClient) === JSON.stringify(editingClient)) {
      toast.error("No changes detected.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      let userIdFromToken = null;

      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1])); // Decode JWT payload
        userIdFromToken = payload.id; // Extract userId from the token
      }

      const endpoint = `${BASE_URL}api/clients-info/update/${editingClient.id}`;
      const payload = {
        ...editingClient,
        userId: editingClient.userId || originalClient.userId || userIdFromToken, // Ensure userId is included
      };

      console.log("Update Payload:", payload); // Log the payload for debugging

      await axios.put(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("The record has been updated successfully.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setClients(
        clients.map((client) =>
          client.id === editingClient.id
            ? { ...client, ...editingClient }
            : client
        )
      ); // Update the client in the list
      setEditingClient(null); // Close the edit mode
      setShowAddModal(false); // Close the modal
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update the record.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setNewInfo({ ...newInfo, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors((prevErrors) => {
        const { [name]: _, ...rest } = prevErrors; // Remove the specific validation error
        return rest;
      });
    }
  };

  const addInformation = async () => {
    const errors = {};
    if (!newInfo.Client || !newInfo.Client.trim()) errors.Client = "Client name is required.";
    if (!newInfo.Domain || !newInfo.Domain.trim()) errors.Domain = "Domain is required.";

    // Validation to ensure at least one other field contains a value
    const otherFields = [
      newInfo.Registrar,
      newInfo.Hosting,
      newInfo.HostingBy,
      newInfo.HostigValue,
      newInfo.WPAdminCredentials,
      newInfo.Emails,
      newInfo.FTPInfo,
      newInfo.SVN,
      newInfo.DatabaseInfo,
      newInfo.Others,
    ];
    const hasAtLeastOneValue = otherFields.some((field) => field && field.trim());
    if (!hasAtLeastOneValue) {
      errors.OtherFields = "At least one additional field must contain a value.";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors); // Set validation errors
      Object.values(errors).forEach((error) => {
        toast.error(error, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      let userIdFromToken = null;

      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1])); // Decode JWT payload
        userIdFromToken = payload.id; // Extract userId from the token
      }

      const payload = { ...newInfo, userId: userIdFromToken }; // Include userId in the payload
      console.log("Data sent to API:", payload); // Log the data being sent to the API
      const endpoint = `${BASE_URL}api/clients-info/create`; // Use the appropriate API endpoint
      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Client information has been added successfully.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setShowAddModal(false); // Close the modal
      setNewInfo({
        Client: "",
        Domain: "",
        Registrar: "",
        Hosting: "",
        HostingBy: "",
        HostigValue: "",
        WPAdminCredentials: "",
        Emails: "",
        FTPInfo: "",
        SVN: "",
        DatabaseInfo: "",
        Others: "",
      }); // Reset form
      setValidationErrors({}); // Clear validation errors
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add client information.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
          <Loader message="Loading client information..." /> {/* Show loader only on the table */}
        </div>
      );
    }

    const filteredClients = clients.filter((client) => {
      return Object.values(client).some((value) => 
        value && 
      (value.toString().toLowerCase().includes(search.toLowerCase())
      )
      );
    });
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>{clientTabs.find((tab) => tab.id === activeTab)?.name || "Softisans"}</h3> {/* Fallback for activeTab */}
          <input
            type="text"
            className="form-control w-50"
            placeholder={`Search ${clientTabs.find((tab) => tab.id === activeTab)?.name?.toLowerCase() || "default"}...`} // Fallback for activeTab
            value={search}
            onChange={(e) => setSearch(e.target.value)} // Update search state
          />
        </div>
        {filteredClients.length === 0 ? (
          <p>No {clientTabs.find((tab) => tab.id === activeTab)?.name?.toLowerCase() || "default"} available.</p> // Fallback for activeTab
        ) : (
          <div className="row">
            {filteredClients.map((client, index) => (
              <div className="col-md-4 mb-3" key={client.id}>
                <div className="card shadow-sm border-0 rounded" style={{ backgroundColor: "#ffffff" }}>
                  {renderProgressBar(index)}
                  <div className="d-flex justify-content-between align-items-center p-3">
                    <h5 className="mb-0">{client.Domain || "Unnamed"}</h5> {/* Fallback for client.name */}
                    <div className="dropdown">
                      <button
                        className="btn btn-link text-dark"
                        type="button"
                        id={`dropdownMenuButton${client.id}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                      </button>
                      <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton${client.id}`}>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              setEditingClient(client); // Set the client being edited
                              setShowAddModal(true); // Show the modal
                            }}
                          >
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => deleteRecord(client.id)} // Call deleteRecord with the client's id
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="card-body py-0">
                    <div
                      className="content-container"
                      style={{
                        maxHeight: expandedCard === client.id ? "100%" : "0", // Adjust height for full expand/collapse
                        overflow: "hidden",
                        transition: "max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1)", // Smooth transition for both expand and collapse
                      }}
                    >
                      {expandedCard === client.id && (
                        <div className="">
                          {Object.entries(client)
                            .filter(([key, value]) => 
                              value && 
                              !["id", "user", "userId", "updated_at", "CreatedAt"].includes(key) && 
                              !(key === "Client") && 
                              !(key === "Domain") // Exclude specific values
                            )
                            .map(([key, value]) => (
                              <div key={key} className="mb-3" style={{ fontSize: "15px" }}>
                                <div className="d-flex align-items-center">
                                  <strong>{key}:</strong>
                                  <FontAwesomeIcon
                                    icon={faLink}
                                    size="sm"
                                    className="ms-2 text-muted" // Light icon
                                    style={{ cursor: "pointer" }}
                                    title={`Copy ${key}`}
                                    onClick={() => copyToClipboardWithToast(key, value)} // Use the new function
                                  />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || "N/A") }} />
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    {expandedCard === client.id && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {client.user?.username ? (
                            <a href={`${window.location.origin}/user-detail?id=${client.user.id}`} title={client.user.username}>
                              <img
                                src="https://lh3.googleusercontent.com/a/AGNmyxabdyjUhShMyEkxB96X86-qnHO-QF7SHvsrnQ49=s96-c"
                                alt="User"
                                className="rounded-circle me-2"
                                style={{ width: "40px", height: "40px" }}
                              />
                            </a>
                          ) : (
                            <img
                              src="https://lh3.googleusercontent.com/a/AGNmyxabdyjUhShMyEkxB96X86-qnHO-QF7SHvsrnQ49=s96-c"
                              alt="User"
                              className="rounded-circle me-2"
                              style={{ width: "40px", height: "40px" }}
                            />
                          )}
                          <FontAwesomeIcon
                            icon={faLink}
                            size="md"
                            className="ms-2 text-muted"
                            style={{ cursor: "pointer" }}
                            title="Copy all content to clipboard"
                            onClick={() => {
                              const cardData = Object.entries(client)
                                .filter(([key, value]) => 
                                  value && 
                                  !["id", "userId", "CreatedAt"].includes(key) // Exclude specific keys
                                )
                                .map(([key, value]) => `${key}: ${DOMPurify.sanitize(value || "N/A").replace(/<[^>]*>/g, "")}`) // Remove HTML tags
                                .join("\n\n"); // Add double newlines for spacing between columns
                              navigator.clipboard.writeText(cardData); // Copy the formatted data
                              toast.success("Content copied to clipboard!", {
                                position: "top-right",
                                autoClose: 2000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                progress: undefined,
                                style: { backgroundColor: "#006400", color: "#ffffff" }, // Dark green background with white text
                              });
                            }}
                          />
                        </div>
                        <small className="text-muted">{formatDate(client.CreatedAt)}</small>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      className="btn btn-link"
                      type="button"
                      onClick={() => toggleCard(client.id)}
                    >
                      <FontAwesomeIcon icon={expandedCard === client.id ? faAngleUp : faAngleDown} size="lg" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mt-4" style={{ backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px" }}> {/* Light background for main container */}
      <div className="mb-3 d-flex justify-content-between">
        <h2>Important Information</h2>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          Add Information
        </button>
      </div>
      <div className="mb-3 d-flex justify-content-between">
        <p>All Clients Information. </p>
      </div>
      {/* Tabs */}
      <ul className="nav nav-tabs mb-3 w-100 d-flex">
        {clientTabs.map((tab) => (
          <li className="nav-item flex-grow-1 text-center" key={tab.id}>
            <button
              className={`nav-link w-100 ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)} // Set activeTab to the client ID
            >
              {tab.name}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab Content */}
      <div className="mt-4">
        {renderTabContent()}
      </div>

      {showAddModal && editingClient && (
        <div className="modal modal-xl show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Client Information</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingClient(null); // Reset editing client
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="editClientName" className="form-label">Client Name</label>
                  <select
                    id="client_name"
                    name="Client"
                    className="form-select"
                    value={editingClient ? editingClient.Client : newInfo.Client} // Dynamically set value based on editing state
                    onChange={(e) => {
                      const value = e.target.value;
                      if (editingClient) {
                        setEditingClient({ ...editingClient, Client: value }); // Update editing client
                      } else {
                        handleAddChange(e); // Update new client info
                      }
                    }}
                    onFocus={() => setValidationErrors((prev) => ({ ...prev, Client: undefined }))}
                    required
                  >
                    <option value="">Select a client</option>
                    {clientsDropdown.map((client) => (
                      <option key={client.name} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="editDomain" className="form-label">Domain</label>
                  <input
                    type="text"
                    id="editDomain"
                    name="Domain"
                    className="form-control"
                    value={editingClient.Domain || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, Domain: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editRegistrar" className="form-label">Registrar</label>
                  <textarea
                    id="editRegistrar"
                    name="Registrar"
                    className="form-control"
                    rows="3"
                    value={editingClient.Registrar || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, Registrar: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editHosting" className="form-label">Hosting</label>
                  <textarea
                    id="editHosting"
                    name="Hosting"
                    className="form-control"
                    rows="3"
                    value={editingClient.Hosting || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, Hosting: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editHostingBy" className="form-label">Hosting By</label>
                  <textarea
                    id="editHostingBy"
                    name="HostingBy"
                    className="form-control"
                    rows="3"
                    value={editingClient.HostingBy || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, HostingBy: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editHostigValue" className="form-label">Hosting Value</label>
                  <textarea
                    id="editHostigValue"
                    name="HostigValue"
                    className="form-control"
                    rows="3"
                    value={editingClient.HostigValue || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, HostigValue: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editWPAdminCredentials" className="form-label">WP Admin Credentials</label>
                  <textarea
                    id="editWPAdminCredentials"
                    name="WPAdminCredentials"
                    className="form-control"
                    rows="3"
                    value={editingClient.WPAdminCredentials || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, WPAdminCredentials: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editEmails" className="form-label">Emails</label>
                  <textarea
                    id="editEmails"
                    name="Emails"
                    className="form-control"
                    rows="3"
                    value={editingClient.Emails || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, Emails: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editFTPInfo" className="form-label">FTP Info</label>
                  <textarea
                    id="editFTPInfo"
                    name="FTPInfo"
                    className="form-control"
                    rows="3"
                    value={editingClient.FTPInfo || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, FTPInfo: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editSVN" className="form-label">SVN</label>
                  <textarea
                    id="editSVN"
                    name="SVN"
                    className="form-control"
                    rows="3"
                    value={editingClient.SVN || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, SVN: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editDatabaseInfo" className="form-label">Database Info</label>
                  <textarea
                    id="editDatabaseInfo"
                    name="DatabaseInfo"
                    className="form-control"
                    rows="3"
                    value={editingClient.DatabaseInfo || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, DatabaseInfo: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="editOthers" className="form-label">Others</label>
                  <textarea
                    id="editOthers"
                    name="Others"
                    className="form-control"
                    rows="3"
                    value={editingClient.Others || ""}
                    onChange={(e) => setEditingClient({ ...editingClient, Others: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingClient(null); // Reset editing client
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={updateRecord} // Call updateRecord to save changes
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && !editingClient && (
        <div className="modal modal-xl show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Client Information</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)} // Close the modal
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="client_name" className="form-label">Client Name</label>
                  <select
                    id="client_name"
                    name="Client"
                    className="form-select"
                    value={editingClient ? editingClient.Client : newInfo.Client} // Dynamically set value based on editing state
                    onChange={(e) => {
                      const value = e.target.value;
                      if (editingClient) {
                        setEditingClient({ ...editingClient, Client: value }); // Update editing client
                      } else {
                        handleAddChange(e); // Update new client info
                      }
                    }}
                    onFocus={() => setValidationErrors((prev) => ({ ...prev, Client: undefined }))}
                    required
                  >
                    <option value="">Select a client</option>
                    {clientsDropdown.map((client) => (
                      <option key={client.name} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.Client && <small className="text-danger">{validationErrors.Client}</small>}
                </div>
                <div className="mb-3">
                  <label htmlFor="Domain" className="form-label">Domain</label>
                  <input
                    type="text"
                    id="Domain"
                    name="Domain"
                    className="form-control"
                    value={newInfo.Domain || ""}
                    onChange={handleAddChange}
                    onFocus={() => setValidationErrors((prev) => ({ ...prev, Domain: undefined }))}
                    required
                  />
                  {validationErrors.Domain && <small className="text-danger">{validationErrors.Domain}</small>}
                </div>
                <div className="mb-3">
                  <label htmlFor="Registrar" className="form-label">Registrar</label>
                  <textarea
                    id="Registrar"
                    name="Registrar"
                    className="form-control"
                    rows="3"
                    value={newInfo.Registrar || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, Registrar: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="Hosting" className="form-label">Hosting</label>
                  <textarea
                    id="Hosting"
                    name="Hosting"
                    className="form-control"
                    rows="3"
                    value={newInfo.Hosting || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, Hosting: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="HostingBy" className="form-label">Hosting By</label>
                  <textarea
                    id="HostingBy"
                    name="HostingBy"
                    className="form-control"
                    rows="3"
                    value={newInfo.HostingBy || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, HostingBy: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="HostigValue" className="form-label">Hosting Value</label>
                  <textarea
                    id="HostigValue"
                    name="HostigValue"
                    className="form-control"
                    rows="3"
                    value={newInfo.HostigValue || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, HostigValue: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="WPAdminCredentials" className="form-label">WP Admin Credentials</label>
                  <textarea
                    id="WPAdminCredentials"
                    name="WPAdminCredentials"
                    className="form-control"
                    rows="3"
                    value={newInfo.WPAdminCredentials || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, WPAdminCredentials: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="Emails" className="form-label">Emails</label>
                  <textarea
                    id="Emails"
                    name="Emails"
                    className="form-control"
                    rows="3"
                    value={newInfo.Emails || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, Emails: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="FTPInfo" className="form-label">FTP Info</label>
                  <textarea
                    id="FTPInfo"
                    name="FTPInfo"
                    className="form-control"
                    rows="3"
                    value={newInfo.FTPInfo || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, FTPInfo: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="SVN" className="form-label">SVN</label>
                  <textarea
                    id="SVN"
                    name="SVN"
                    className="form-control"
                    rows="3"
                    value={newInfo.SVN || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, SVN: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="DatabaseInfo" className="form-label">Database Info</label>
                  <textarea
                    id="DatabaseInfo"
                    name="DatabaseInfo"
                    className="form-control"
                    rows="3"
                    value={newInfo.DatabaseInfo || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, DatabaseInfo: e.target.value })}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label htmlFor="Others" className="form-label">Others</label>
                  <textarea
                    id="Others"
                    name="Others"
                    className="form-control"
                    rows="3"
                    value={newInfo.Others || ""}
                    onChange={(e) => setNewInfo({ ...newInfo, Others: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)} // Close the modal
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addInformation} // Call addInformation to save data
                >
                  Add Client Information
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
};

export default ClientsInformation;
